import React, { useCallback, useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { FieldControl, FormBuilder } from "react-reactive-form";
import { CsrAPI } from "../apis/CsrAPI";
import { useTableStatusPolling } from "../hooks/useTableStatusPolling";

/**
 * MultiSelectDropdown Component
 *
 * A reusable multi-select dropdown component using react-select/async that integrates
 * with the existing form management system using react-reactive-form and
 * dynamically fetches table names from API endpoints.
 *
 * Props:
 * - controlName: string (identifier for the form control)
 * - formGroup: object (contains form state and methods)
 * - disabled: boolean (optional, defaults to false)
 * - studyId: string (study ID for API calls)
 *
 * Data Structure:
 * The component maintains compatibility with the existing data structure:
 * - Form submission format: [{"valueHelper": "Overall Survival"}, {"valueHelper": "Progression-free Survival"}]
 * - React-select format: [{value: "Overall Survival", label: "Overall Survival"}]
 *
 * API Integration:
 * 1. Checks extraction status: GET /studies/{study_id}/table_names/extraction_status
 * 2. Fetches table names: GET /studies/{study_id}/table_names (when ready)
 * 3. Handles loading and error states gracefully
 *
 * The component automatically converts between these formats:
 * 1. When user selects options: react-select format → submission format
 * 2. When displaying initial values: submission format → react-select format
 *
 * Form Integration:
 * - Works with FormArray controls in react-reactive-form
 * - Automatically clears and rebuilds FormArray when selections change
 * - Maintains proper form state synchronization
 */

const MultiSelectDropdown = ({
  controlName,
  formGroup,
  disabled = false,
  studyId,
  placeholder = "Type To Search Tables or Select Tables From List",
  noOptionsMessage = "No Matches Found",
  loadingMessage = "Loading Tables...",
}) => {
  const [tableNamesCache, setTableNamesCache] = useState({});

  // Use the polling hook for table status
  const { isReady: isTableStatusReady } = useTableStatusPolling(studyId, 5000);

  // Load options asynchronously
  const loadOptions = useCallback(
    async (inputValue, isTableDataReady) => {
      if (!studyId) {
        return [];
      }

      try {
        // Check if we already have cached data for this study
        if (tableNamesCache[studyId]) {
          const cachedData = tableNamesCache[studyId];
          return filterOptions(cachedData, inputValue);
        }

        // Use the polling hook status instead of manual API call
        if (isTableDataReady) {
          // 2. Fetch table names if ready
          const tablesRes = await CsrAPI.getTableNames({ studyId });
          const tableNames = [...new Set(tablesRes)] || [];

          // Cache the results
          const formattedData = formatTableNames(tableNames);
          setTableNamesCache(prev => ({
            ...prev,
            [studyId]: formattedData,
          }));

          return filterOptions(formattedData, inputValue);
        } else {
          // Data not ready yet, return empty array
          return [];
        }
      } catch (err) {
        console.error("Error fetching table names:", err);
        return [];
      }
    },
    [studyId, tableNamesCache, isTableStatusReady]
  );

  // Effect to automatically load options when table status becomes ready
  useEffect(() => {
    if (isTableStatusReady && !tableNamesCache[studyId]) {
      // Trigger loadOptions to populate cache
      loadOptions("", isTableStatusReady);
    }
  }, [isTableStatusReady, studyId, tableNamesCache, loadOptions]);

  // Helper function to format table names
  const formatTableNames = tableNames => {
    if (!tableNames || !Array.isArray(tableNames)) {
      return [];
    }

    return tableNames
      .filter(item => item && typeof item === "string")
      .map(item => ({
        value: item,
        label: item,
        valueHelper: item,
      }));
  };

  // Helper function to filter options based on input value
  const filterOptions = (options, inputValue) => {
    if (!inputValue) {
      return options;
    }

    return options.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  // Convert react-select format to our required format for form submission
  const convertToSubmissionFormat = selectedOptions => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return [];
    }
    return selectedOptions.map(option => ({
      valueHelper: option.value,
    }));
  };

  // Convert from our format to react-select format for initial values
  const convertFromSubmissionFormat = formValue => {
    if (!formValue || !Array.isArray(formValue) || formValue.length === 0) {
      return [];
    }
    return formValue
      .filter(item => item && item.valueHelper)
      .map(item => ({
        value: item.valueHelper,
        label: item.valueHelper,
      }));
  };

  // Custom styles for react-select to match the application's design
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
      borderRadius: "0.375rem",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      "&:hover": {
        border: "1px solid #86b7fe",
      },
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
    }),
    multiValue: provided => ({
      ...provided,
      backgroundColor: "#e9ecef",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: "#495057",
      fontSize: "0.875rem",
    }),
    multiValueRemove: provided => ({
      ...provided,
      color: "#6c757d",
      "&:hover": {
        backgroundColor: "#dc3545",
        color: "white",
      },
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
    }),
    container: provided => ({
      ...provided,
      width: "100%",
    }),
  };

  return (
    <FieldControl
      name={controlName}
      render={() => {
        const formControl = formGroup.get(controlName);
        const currentValue = formControl?.value || [];
        const selectedOptions = convertFromSubmissionFormat(currentValue);

        return (
          <div className="multiselect-container">
            <AsyncSelect
              isMulti
              closeMenuOnSelect={false}
              loadOptions={loadOptions}
              value={selectedOptions}
              onChange={selectedOptions => {
                const submissionFormat =
                  convertToSubmissionFormat(selectedOptions);

                // Clear existing FormArray
                while (formControl.length > 0) {
                  formControl.removeAt(0);
                }

                // Add new values to FormArray
                submissionFormat.forEach(item => {
                  formControl.push(FormBuilder.control(item));
                });
              }}
              styles={customStyles}
              placeholder={placeholder}
              isClearable={true}
              isSearchable={true}
              isDisabled={disabled}
              className="multiselect-dropdown"
              classNamePrefix="multiselect"
              noOptionsMessage={({ inputValue }) => {
                if (inputValue) {
                  return noOptionsMessage;
                }
                return placeholder;
              }}
              loadingMessage={() => loadingMessage}
              cacheOptions={true}
              defaultOptions={true}
            />
          </div>
        );
      }}
    />
  );
};

export default MultiSelectDropdown;
